import React, { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  SITE_PREVIEW_PATH,
  SITE_SECTIONS,
  isFromFrame,
  postToFrame,
} from "../../utils/sitePreview";
import DeviceFrame, { DeviceToggle, initialDevice } from "./DeviceFrame";

/**
 * The live preview card: the real public page, laid out at a desktop or a
 * phone width and scaled down to fit the column it sits in.
 *
 * Everything it shows comes from `settings` — the draft, not what was last
 * saved — and is redrawn on every change. With `onSelectSection`, a click on a
 * section of the page selects that section for editing.
 */
const SitePreview = ({
  settings,
  showsBadge = false,
  section = null,
  block = null,
  onSelectSection = null,
  address = null,
  actions = null,
  status = null,
  defaultDevice = null,
  headingLevel = 2,
  className = "",
}) => {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const frameRef = useRef(null);
  const onSelectRef = useRef(onSelectSection);
  const [device, setDevice] = useState(() => initialDevice(defaultDevice));
  const [readyCount, setReadyCount] = useState(0);

  onSelectRef.current = onSelectSection;
  const editable = Boolean(onSelectSection);

  // "ready" arrives every time the frame (re)loads; each one is answered with
  // the current draft, so a reload never leaves it blank.
  useEffect(() => {
    const onMessage = (event) => {
      if (!isFromFrame(event, frameRef.current?.contentWindow)) return;
      if (event.data.type === "ready") setReadyCount((count) => count + 1);
      if (event.data.type === "select" && SITE_SECTIONS.includes(event.data.section)) {
        const { block } = event.data;
        onSelectRef.current?.(event.data.section, Number.isInteger(block) && block >= 0 ? block : null);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const lang = i18n?.language;
  useEffect(() => {
    if (!readyCount) return;
    postToFrame(frameRef.current?.contentWindow, "render", {
      settings,
      showsBadge,
      section,
      block,
      editable,
      lang,
    });
  }, [readyCount, settings, showsBadge, section, block, editable, lang]);

  const ready = readyCount > 0;
  const Heading = headingLevel === 3 ? "h3" : "h2";

  return (
    <section
      className={`min-w-0 overflow-hidden rounded-card border border-ink-200 bg-white shadow-card ${className}`}
      aria-labelledby={titleId}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-ink-200 px-4 py-3 sm:px-5">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <Heading id={titleId} className="m-0 text-base font-semibold text-ink-900">
            {t("Dashboard.MySite.preview.title")}
          </Heading>
          {actions}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {status}
          <DeviceToggle device={device} onChange={setDevice} />
        </div>
      </div>

      <div className="bg-ink-50 p-3 sm:p-4">
        <DeviceFrame device={device} bar={address ? { address } : null}>
          {({ width, height }) => (
            <>
              <iframe
                ref={frameRef}
                src={SITE_PREVIEW_PATH}
                title={t("Dashboard.MySite.preview.frameTitle")}
                data-testid="site-preview-frame"
                data-device={device}
                className="block border-0 bg-white"
                style={{ width: `${width}px`, height: `${height}px` }}
              />
              {!ready && (
                <div
                  role="status"
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white text-sm text-ink-500"
                >
                  <span
                    className="size-8 animate-spin rounded-full border-3 border-ink-200 border-t-brand-600 motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                  {t("Dashboard.MySite.preview.loading")}
                </div>
              )}
            </>
          )}
        </DeviceFrame>
        {editable && (
          <p className="mx-auto mt-3 mb-0 flex max-w-md items-center justify-center gap-1.5 text-center text-xs text-ink-500">
            <i className="bi bi-hand-index" aria-hidden="true" />
            {t("Dashboard.MySite.preview.clickHint")}
          </p>
        )}
      </div>
    </section>
  );
};

SitePreview.propTypes = {
  /** BookingPageSettings as being edited, plus `businessName`. */
  settings: PropTypes.object.isRequired,
  showsBadge: PropTypes.bool,
  /** The section being edited, outlined in the page. */
  section: PropTypes.oneOf(SITE_SECTIONS),
  /** Within About, the block being edited (its index), outlined in the page. */
  block: PropTypes.number,
  /** Beside the device switch, e.g. whether the HTML shown is the applied one. */
  status: PropTypes.node,
  /** Makes the page's sections clickable; called with the section's id. */
  onSelectSection: PropTypes.func,
  /** The public address, shown in the desktop frame's address bar. */
  address: PropTypes.string,
  /** Beside the title, e.g. a link to the live page. */
  actions: PropTypes.node,
  /** Without one: a phone where the screen is phone-sized, else desktop. */
  defaultDevice: PropTypes.oneOf(["desktop", "mobile"]),
  /** The title's level in the page around it. */
  headingLevel: PropTypes.oneOf([2, 3]),
  className: PropTypes.string,
};

export default SitePreview;
